/**
 * Ground Truth Store for Enhanced Segmentation Agent v2.0
 * 
 * This store manages ground truth file management, validation, and selection
 * functionality for accurate micro-unit segmentation evaluation.
 * 
 * Extracted from segmentationStore.ts to provide focused ground truth
 * capabilities with better organization and testability.
 * 
 * Author: Enhanced Segmentation Agent v2.0
 * Date: June 26, 2025
 */

import { create } from 'zustand'
import { API_ENDPOINTS } from '../config/api'
import type { 
  GroundTruthFile,
  GroundTruthInfo,
  LLMConfig
} from '../types/segmentationTypes'

// ============================================================================
// Ground Truth Store State Interface
// ============================================================================

interface GroundTruthStoreState {
  // Ground truth state
  groundTruthFiles: GroundTruthFile[]
  selectedGroundTruth: GroundTruthFile | null
  groundTruthInfo: GroundTruthInfo | null
  groundTruthLoading: boolean
  groundTruthError: string | null
  
  // Actions
  loadGroundTruthInfo: () => Promise<void>
  uploadGroundTruthFile: (fileOrData: File | any) => Promise<void>
  selectGroundTruth: (groundTruthId: string | null) => void
  removeGroundTruthFile: (groundTruthId: string) => void
  validateGroundTruthFile: (content: any) => boolean
  clearGroundTruthError: () => void
  resetGroundTruthState: () => void
  
  // Ground truth mode management
  enableGroundTruthMode: (llmConfig: LLMConfig) => LLMConfig
  disableGroundTruthMode: (llmConfig: LLMConfig) => LLMConfig
}

// ============================================================================
// Ground Truth Store Implementation
// ============================================================================

export const useGroundTruthStore = create<GroundTruthStoreState>((set, get) => ({
  // Initial state
  groundTruthFiles: [],
  selectedGroundTruth: null,
  groundTruthInfo: null,
  groundTruthLoading: false,
  groundTruthError: null,

  // ============================================================================
  // Ground Truth Information Loading
  // ============================================================================

  loadGroundTruthInfo: async (): Promise<void> => {
    set({ groundTruthLoading: true, groundTruthError: null })
    
    try {
      const response = await fetch(API_ENDPOINTS.groundTruth.info)
      
      if (!response.ok) {
        throw new Error(`Failed to load ground truth info: ${response.statusText}`)
      }
      
      const info = await response.json()
      set({ 
        groundTruthInfo: info, 
        groundTruthLoading: false 
      })
      
      console.log('Ground truth info loaded successfully:', info)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load ground truth info'
      set({ 
        groundTruthError: errorMessage,
        groundTruthLoading: false 
      })
      console.error('Failed to load ground truth info:', error)
    }
  },

  // ============================================================================
  // Ground Truth File Upload
  // ============================================================================

  uploadGroundTruthFile: async (fileOrData: File | any): Promise<void> => {
    set({ groundTruthLoading: true, groundTruthError: null })
    
    try {
      let content: any
      let fileName: string
      let isSessionData = false
      
      // Check if it's a File object or pre-loaded session data
      if (fileOrData instanceof File) {
        const text = await fileOrData.text()
        content = JSON.parse(text)
        fileName = fileOrData.name
      } else {
        // It's already parsed session data
        content = fileOrData.data || fileOrData
        fileName = fileOrData.name || 'Session Data'
        isSessionData = true
      }
      
      const { validateGroundTruthFile } = get()
      if (!validateGroundTruthFile(content)) {
        throw new Error('Invalid ground truth file format')
      }

      // Extract metadata from the ground truth file
      const segmentCount = content.chunk_results?.reduce((total: number, chunk: any) => {
        return total + (chunk.micro_units?.length || 0)
      }, 0) || 0

      const witnessName = content.segmentation_metadata?.text_identifier || 
                          (isSessionData ? fileOrData.witnessName : null) ||
                          fileName.replace(/\.(json|txt)$/i, '')

      const groundTruthFile: GroundTruthFile = {
        id: isSessionData ? fileOrData.id : `gt_${Date.now()}`,
        name: fileName,
        content,
        uploadedAt: isSessionData ? new Date(fileOrData.uploadedAt).getTime() : Date.now(),
        witnessName,
        segmentCount
      }

      set(state => ({ 
        groundTruthFiles: [...state.groundTruthFiles, groundTruthFile],
        groundTruthLoading: false
      }))

      console.log('Ground truth file uploaded:', groundTruthFile.name, `${segmentCount} segments`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload ground truth file'
      set({ 
        groundTruthError: errorMessage,
        groundTruthLoading: false 
      })
      console.error('Failed to upload ground truth file:', error)
      throw error
    }
  },

  // ============================================================================
  // Ground Truth Selection
  // ============================================================================

  selectGroundTruth: (groundTruthId: string | null): void => {
    const state = get()
    const selectedFile = groundTruthId ? 
      state.groundTruthFiles.find(f => f.id === groundTruthId) || null : 
      null

    set({ selectedGroundTruth: selectedFile })
    console.log('Selected ground truth:', selectedFile?.name || 'None')
  },

  // ============================================================================
  // Ground Truth File Management
  // ============================================================================

  removeGroundTruthFile: (groundTruthId: string): void => {
    const state = get()
    const newFiles = state.groundTruthFiles.filter(f => f.id !== groundTruthId)
    
    // If the removed file was selected, clear selection
    const newSelectedGroundTruth = state.selectedGroundTruth?.id === groundTruthId ? 
      null : state.selectedGroundTruth

    set({ 
      groundTruthFiles: newFiles,
      selectedGroundTruth: newSelectedGroundTruth
    })
    
    console.log('Ground truth file removed:', groundTruthId)
  },

  // ============================================================================
  // Ground Truth File Validation
  // ============================================================================

  validateGroundTruthFile: (content: any): boolean => {
    try {
      // Check if it has the required structure of a CompleteMicroSegmentationResult
      if (!content || typeof content !== 'object') {
        console.warn('Ground truth validation failed: Invalid content type')
        return false
      }
      
      // Check for required top-level fields
      if (!content.chunk_results || !Array.isArray(content.chunk_results)) {
        console.warn('Ground truth validation failed: Missing or invalid chunk_results')
        return false
      }
      
      if (!content.segmentation_metadata || typeof content.segmentation_metadata !== 'object') {
        console.warn('Ground truth validation failed: Missing or invalid segmentation_metadata')
        return false
      }
      
      if (!content.aggregated_statistics || typeof content.aggregated_statistics !== 'object') {
        console.warn('Ground truth validation failed: Missing or invalid aggregated_statistics')
        return false
      }
      
      // Check that chunk_results contains valid data
      if (content.chunk_results.length === 0) {
        console.warn('Ground truth validation failed: Empty chunk_results')
        return false
      }
      
      // Validate at least one chunk result has the expected structure
      const firstChunk = content.chunk_results[0]
      if (!firstChunk.source_chunk_id || !firstChunk.micro_units || !Array.isArray(firstChunk.micro_units)) {
        console.warn('Ground truth validation failed: Invalid chunk structure')
        return false
      }
      
      // If there are micro units, validate one has the expected structure
      if (firstChunk.micro_units.length > 0) {
        const firstUnit = firstChunk.micro_units[0]
        if (!firstUnit.unit_id || !firstUnit.unit_text_content || 
            typeof firstUnit.start_position !== 'number' || 
            typeof firstUnit.end_position !== 'number') {
          console.warn('Ground truth validation failed: Invalid micro unit structure')
          return false
        }
      }
      
      console.log('Ground truth validation passed')
      return true
    } catch (error) {
      console.error('Error validating ground truth file:', error)
      return false
    }
  },

  // ============================================================================
  // Ground Truth Mode Management
  // ============================================================================

  enableGroundTruthMode: (llmConfig: LLMConfig): LLMConfig => {
    return {
      ...llmConfig,
      ground_truth_mode: true
    }
  },

  disableGroundTruthMode: (llmConfig: LLMConfig): LLMConfig => {
    return {
      ...llmConfig,
      ground_truth_mode: false
    }
  },

  // ============================================================================
  // Utility Actions
  // ============================================================================

  clearGroundTruthError: (): void => {
    set({ groundTruthError: null })
  },

  resetGroundTruthState: (): void => {
    set({
      groundTruthFiles: [],
      selectedGroundTruth: null,
      groundTruthInfo: null,
      groundTruthLoading: false,
      groundTruthError: null
    })
  }
}))

// ============================================================================
// Export for use in other components
// ============================================================================

export default useGroundTruthStore